/**
 * Shared focus / tab-order utilities used by the Harmony keyboard navigation directives.
 *
 * These helpers implement the application-wide rule that pressing `Tab` must move focus to the next _applicable
 * editable control_, following the visual and functional order of the page while skipping disabled, hidden and
 * read-only controls.
 */

/**
 * CSS selector matching every element that is potentially an editable / interactive control.
 *
 * Intentionally broad: the candidate list produced by this selector is refined afterwards by {@link isEditableControl}
 * which applies the disabled / hidden / read-only rules.
 */
export const FOCUSABLE_CONTROL_SELECTOR = [
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'button',
  'a[href]',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="radiogroup"]',
  '[data-hx-tab-self-managed]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="textbox"]',
].join(',');

/** Attribute an application can place on a control to force it out of the managed tab sequence. */
export const SKIP_TAB_ATTRIBUTE = 'data-hx-tab-skip';

/** Attribute an application can place on a container to opt its subtree out of managed tab navigation. */
export const SKIP_TAB_REGION_ATTRIBUTE = 'data-hx-tab-skip-region';

/** Element types whose value can be edited by typing. */
const TEXTUAL_INPUT_TYPES = new Set([
  'text',
  'search',
  'url',
  'tel',
  'email',
  'password',
  'number',
  'date',
  'datetime-local',
  'month',
  'week',
  'time',
]);

/** Returns the deepest active element, piercing any open shadow roots. */
export function getActiveElement(root: Document | ShadowRoot = document): HTMLElement | null {
  const active = root.activeElement as HTMLElement | null;
  if (active?.shadowRoot) {
    return getActiveElement(active.shadowRoot) ?? active;
  }
  return active;
}

/** Whether the element (or an ancestor) is explicitly marked as disabled. */
export function isDisabled(element: HTMLElement): boolean {
  if ((element as HTMLInputElement).disabled) {
    return true;
  }
  if (element.getAttribute('aria-disabled') === 'true') {
    return true;
  }
  // `fieldset[disabled]` disables all nested controls.
  return element.closest('fieldset[disabled], [aria-disabled="true"]') !== null;
}

/** Whether the element is read-only and therefore not an editable target. */
export function isReadOnly(element: HTMLElement): boolean {
  const readOnlyProp = (element as HTMLInputElement).readOnly;
  if (readOnlyProp === true) {
    return true;
  }
  if (element.hasAttribute('readonly')) {
    const val = element.getAttribute('readonly');
    if (val !== 'false') {
      return true;
    }
  }
  return element.getAttribute('aria-readonly') === 'true';
}

/** Whether the element is visually hidden, collapsed or removed from the accessibility tree. */
export function isHidden(element: HTMLElement): boolean {
  if (element.hasAttribute('hidden')) {
    return true;
  }
  if (element.closest('[aria-hidden="true"], [hidden], [inert]') !== null) {
    return true;
  }

  // `offsetParent` is null for `display: none` subtrees (and for `position: fixed`, handled by the rect check).
  const rects = element.getClientRects();
  if (rects.length === 0) {
    // Accessible visually-hidden inputs (such as opacity: 0 native checkbox/radio inputs with 0x0 dimensions)
    // have 0 client rects, but they are fully interactive focus targets if their container is visible.
    if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
      const container = (element.closest('label, .hx-checkbox-container, .hx-radio__container') ??
        element.parentElement) as HTMLElement | null;
      if (container && container.getClientRects().length > 0) {
        const containerStyle = getComputedStyle(container);
        if (
          containerStyle.visibility !== 'hidden' &&
          containerStyle.visibility !== 'collapse' &&
          containerStyle.display !== 'none'
        ) {
          const style = getComputedStyle(element);
          return style.visibility === 'hidden' || style.visibility === 'collapse' || style.display === 'none';
        }
      }
    }
    return true;
  }

  const style = getComputedStyle(element);
  return style.visibility === 'hidden' || style.visibility === 'collapse' || style.display === 'none';
}

/**
 * Whether the element qualifies as an "applicable editable control" for Tab navigation.
 *
 * Skips disabled, hidden, read-only and explicitly opted-out controls, and ignores elements that are only focusable
 * programmatically (`tabindex="-1"`) unless `includeProgrammatic` is set — which the grid navigation uses so that
 * roving-tabindex cells remain reachable column by column.
 */
export function isEditableControl(element: HTMLElement, includeProgrammatic = false): boolean {
  if (element.hasAttribute(SKIP_TAB_ATTRIBUTE)) {
    return false;
  }
  if (element.closest(`[${SKIP_TAB_REGION_ATTRIBUTE}]`) !== null) {
    return false;
  }
  if (isDisabled(element) || isReadOnly(element) || isHidden(element)) {
    return false;
  }

  if (!includeProgrammatic) {
    const tabIndexAttr = element.getAttribute('tabindex');
    if (tabIndexAttr !== null && Number(tabIndexAttr) < 0) {
      // In a radio group, unselected radios carry roving tabindex="-1".
      // If no radio in the group has tabindex="0", allow the first non-disabled radio to be an editable control.
      if (element.matches('[role="tab"], .hx-tabbed-view__tab')) {
        const tablist = element.closest('[role="tablist"], hx-tabbed-view, .hx-tabbed-view__tabs-list');
        if (tablist) {
          return true;
        }
      }

      if (element.matches('[role="radio"], input[type="radio"]')) {
        const group = element.closest('[role="radiogroup"]');
        if (group) {
          const hasZero = group.querySelector('[role="radio"][tabindex="0"], input[type="radio"][tabindex="0"]');
          if (!hasZero) {
            const firstRadio = group.querySelector(
              '[role="radio"]:not([aria-disabled="true"]), input[type="radio"]:not(:disabled)'
            );
            if (firstRadio === element) {
              return true;
            }
          }
        }
      }
      return false;
    }
  }

  return true;
}

/**
 * Normalizes candidate controls within radio groups that have projected or nested sub-controls.
 *
 * In controls like HxRadioWithOther, sub-controls (e.g. AddAllergyControl) are projected under the active radio option.
 * In the DOM, these sub-controls precede subsequent radio options (such as "No"), but in CSS flex/grid layout they are
 * placed with `order: 2` beneath all radio buttons (`order: 1`).
 *
 * This ensures:
 *
 * 1. All radio options in a group are visited together first.
 * 2. Nested sub-controls under the selected option follow the group's radio options.
 * 3. Tabbing from the last sub-control (e.g. Delete button) exits directly to the next control/subsection.
 */
function normalizeRadioSubControlOrder(candidates: HTMLElement[]): HTMLElement[] {
  const radioGroups = new Set<HTMLElement>();
  for (const candidate of candidates) {
    if (candidate.matches('[role="radio"], input[type="radio"]') || candidate.closest('[role="radio"]') !== null) {
      const group = candidate.closest<HTMLElement>('[role="radiogroup"], .hx-radio-group');
      if (group) {
        radioGroups.add(group);
      }
    }
  }

  if (radioGroups.size === 0) {
    return candidates;
  }

  const result: HTMLElement[] = [];
  const processedGroups = new Set<HTMLElement>();

  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    const group = candidate.closest<HTMLElement>('[role="radiogroup"], .hx-radio-group');

    if (!group || !radioGroups.has(group)) {
      result.push(candidate);
      continue;
    }

    if (processedGroups.has(group)) {
      continue;
    }

    processedGroups.add(group);

    const groupCandidates = candidates.filter((c) => c.closest('[role="radiogroup"], .hx-radio-group') === group);
    const radios: HTMLElement[] = [];
    const subControls: HTMLElement[] = [];

    for (const c of groupCandidates) {
      if (c.matches('[role="radio"], input[type="radio"]') || c.closest('[role="radio"]') !== null) {
        radios.push(c);
      } else {
        subControls.push(c);
      }
    }

    result.push(...radios, ...subControls);
  }

  return result;
}

/**
 * Collects the applicable editable controls inside `root`, ordered the way the user perceives them.
 *
 * @param root Container to search.
 * @param order `'dom'` keeps the natural document order (which already matches the visual order for standard flow
 *   layouts); `'visual'` additionally sorts by geometry, which is required for flex/grid layouts whose visual order
 *   differs from the source order.
 * @param includeProgrammatic Include `tabindex="-1"` controls (used for roving-tabindex grids).
 */
export function getEditableControls(
  root: HTMLElement,
  order: 'dom' | 'visual' = 'dom',
  includeProgrammatic = false
): HTMLElement[] {
  const rawCandidates = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR)).filter((element) =>
    isEditableControl(element, includeProgrammatic)
  );
  const candidates = normalizeRadioSubControlOrder(rawCandidates);

  if (order === 'dom') {
    return candidates;
  }

  return candidates
    .map((element, index) => ({ element, index, rect: element.getBoundingClientRect() }))
    .sort((a, b) => {
      // Treat elements whose vertical ranges overlap as being on the same visual row (left-to-right ordering).
      const rowTolerance = Math.max(1, Math.min(a.rect.height, b.rect.height) / 2);
      const sameRow = Math.abs(a.rect.top - b.rect.top) <= rowTolerance;

      if (!sameRow) {
        return a.rect.top - b.rect.top;
      }
      if (a.rect.left !== b.rect.left) {
        return a.rect.left - b.rect.left;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.element);
}

/**
 * Finds the index of the control that currently owns focus.
 *
 * Falls back to the closest ancestor that is part of the list, so composite controls (which move focus to an inner
 * element) are still located correctly.
 */
export function indexOfFocused(controls: HTMLElement[], active: HTMLElement | null): number {
  if (!active) {
    return -1;
  }

  const direct = controls.indexOf(active);
  if (direct !== -1) {
    return direct;
  }

  return controls.findIndex((control) => control.contains(active));
}

/**
 * Composite widgets whose members are navigated with the arrow keys, not with `Tab`.
 *
 * Menus, tab lists, and trees remain single tab stops navigated internally. Radio groups and chip groups are treated as
 * sequential tab stops where Tab visits each option and Enter/Space commits selection.
 */
export const COMPOSITE_GROUP_SELECTOR = [
  '[data-hx-tab-self-managed]',
  '[role="menu"]',
  '[role="menubar"]',
  '[role="tablist"]',

  '[role="tree"]',
].join(',');

/**
 * Single widgets that own their inner focusable implementation details.
 *
 * `hx-radio`, for example, exposes `role="radio"` on its host and renders a native `<input>` inside it. Both match
 * {@link FOCUSABLE_CONTROL_SELECTOR}, but together they are one control and therefore one tab stop.
 */
export const COMPOSITE_WIDGET_SELECTOR = [
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="combobox"]',
  '[role="option"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  'input[type="radio"]',
  'input[type="checkbox"]',
].join(',');

/** The outermost element between `element` and `root` (inclusive) that matches `selector`. */
function outermostMatch(root: HTMLElement, element: HTMLElement, selector: string): HTMLElement | null {
  let match: HTMLElement | null = null;

  for (let node: HTMLElement | null = element; node && node !== root.parentElement; node = node.parentElement) {
    if (node.matches(selector)) {
      match = node;
    }
    if (node === root) {
      break;
    }
  }

  return match;
}

/**
 * The element that owns `control` in the tab sequence: the composite group it belongs to, otherwise the composite
 * widget it is part of, otherwise the control itself.
 */
function tabStopOwner(root: HTMLElement, control: HTMLElement): HTMLElement {
  return (
    outermostMatch(root, control, COMPOSITE_GROUP_SELECTOR) ??
    outermostMatch(root, control, COMPOSITE_WIDGET_SELECTOR) ??
    control
  );
}

/**
 * The element that should actually receive focus for a given owner.
 *
 * A focusable owner (the `role="listbox"` container of a chip group, which also handles the arrow keys) is used
 * directly. Otherwise the owner is a non-focusable wrapper such as a radio group host, and the member carrying
 * `tabindex="0"` — the roving tabindex entry point, i.e. the selected option — is used instead.
 */
function entryPointOf(owner: HTMLElement, members: HTMLElement[], backwards = false): HTMLElement {
  // If owner is a composite group (radiogroup, self-managed table grid, etc.) that is not focusable itself,
  // focus must enter its first interactive member, not the non-focusable container itself.
  const isCompositeContainer = owner.matches(
    '[role="radiogroup"], [role="tablist"], [data-hx-tab-self-managed], [role="table"], table'
  );

  if (members.includes(owner) && !isCompositeContainer) {
    return owner;
  }

  const owned = members.filter((member) => member !== owner && owner.contains(member));
  if (owned.length > 0) {
    if (owner.matches('[role="tablist"]')) {
      return backwards ? owned[owned.length - 1] : owned[0];
    }
    if (owner.matches('[role="radiogroup"], [role="listbox"]')) {
      return owned.find((member) => member.getAttribute('tabindex') === '0') ?? owned[0];
    }
    return backwards ? owned[owned.length - 1] : owned[0];
  }

  // Fallback: If no candidate was matched inside the composite owner (e.g. roving tabindex left all members at tabindex="-1"),
  // find the first non-disabled radio/interactive element inside owner so focus enters the group instead of being dropped.
  const fallback =
    owner.querySelector<HTMLElement>(
      '[role="radio"]:not([aria-disabled="true"]):not([tabindex="-1"]), [role="radio"]:not([aria-disabled="true"]), input:not([type="hidden"]):not(:disabled), button:not(:disabled), select:not(:disabled), textarea:not(:disabled)'
    ) ?? owner.querySelector<HTMLElement>(FOCUSABLE_CONTROL_SELECTOR);

  return fallback ?? owner;
}

/**
 * Collects the logical `Tab` sequence of `root`: exactly one stop per control, in the order the user perceives them.
 *
 * Where {@link getEditableControls} returns every focusable element, this collapses the implementation details of
 * composite controls so a four-option checkbox question is four stops, a radio group is one, and a chip group is one.
 *
 * @param root Container to search.
 * @param order See {@link getEditableControls}.
 * @param includeProgrammatic See {@link getEditableControls}.
 * @param backwards When entering composite multi-control groups like tables in reverse, land on the last control.
 */
export function getTabStops(
  root: HTMLElement,
  order: 'dom' | 'visual' = 'dom',
  includeProgrammatic = false,
  backwards = false
): HTMLElement[] {
  const controls = getEditableControls(root, order, includeProgrammatic);
  const owners: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();

  for (const control of controls) {
    const owner = tabStopOwner(root, control);
    if (seen.has(owner)) {
      continue;
    }
    seen.add(owner);
    owners.push(owner);
  }

  // A focusable element that merely wraps other stops (such as the `hx-checkbox` host around its option inputs) is a
  // container, not a control of its own, so it is dropped in favour of the controls it contains.
  return owners
    .filter((owner) => seen.has(owner) && !owners.some((other) => other !== owner && owner.contains(other)))
    .map((owner) => entryPointOf(owner, controls, backwards));
}

/**
 * Index of the tab stop that currently owns focus, resolving focus held by any member of a composite control back to
 * that control's single stop.
 */
export function indexOfTabStop(root: HTMLElement, stops: HTMLElement[], active: HTMLElement | null): number {
  if (!active) {
    return -1;
  }

  const direct = indexOfFocused(stops, active);
  if (direct !== -1) {
    return direct;
  }

  const owner = tabStopOwner(root, active);
  return stops.findIndex((stop) => stop === owner || owner.contains(stop));
}

export function activateSearchControl(element: HTMLElement): boolean {
  if (!element.isConnected || !isEditableControl(element)) {
    return false;
  }
  const owner = element.closest<HTMLElement>('[data-hx-search-activation]');
  if (!owner) {
    if (element.matches('input[type="search"]')) {
      element.focus({ preventScroll: true });
      return true;
    }
    return false;
  }
  const event = new Event('hx-search-activate', { cancelable: true });
  owner.dispatchEvent(event);
  return event.defaultPrevented;
}

export function focusFirstEditableControl(root: HTMLElement, fieldSelector?: string): boolean {
  const field = getEditableControls(root).find((candidate) => !fieldSelector || candidate.closest(fieldSelector));
  if (!field) {
    return false;
  }
  if (!activateSearchControl(field)) {
    field.focus({ preventScroll: true });
  }
  return true;
}

/** Moves focus to the control, selecting existing text so the user can immediately overwrite it. */
export function focusControl(element: HTMLElement): void {
  try {
    element.focus({ preventScroll: false, focusVisible: true } as FocusOptions);
  } catch {
    element.focus({ preventScroll: false });
  }

  const input = element as HTMLInputElement | HTMLTextAreaElement;
  const type = (input as HTMLInputElement).type;
  const isTextual =
    element.tagName === 'TEXTAREA' ||
    (element.tagName === 'INPUT' && TEXTUAL_INPUT_TYPES.has(type) && type !== 'number');

  if (isTextual && typeof input.select === 'function') {
    try {
      input.select();
    } catch {
      // `select()` throws for input types that do not support text selection; safe to ignore.
    }
  }
}

/** Whether the keyboard event is a plain Tab / Shift+Tab (no modifier keys that imply browser shortcuts). */
export function isPlainTab(event: KeyboardEvent): boolean {
  return event.key === 'Tab' && !event.ctrlKey && !event.altKey && !event.metaKey;
}
